import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import MatchCard from "@/components/MatchCard";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookmarkCheck, Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STARS = Array.from({ length: 28 }, () => {
  const top = Math.random() * 100;
  const left = Math.random() * 100;
  return {
    id: `sl-${top.toFixed(3)}-${left.toFixed(3)}`,
    top, left,
    size: Math.random() * 2 + 1,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 4,
  };
});

function StarField() {
  return (
    <div className="starfield" aria-hidden>
      {STARS.map((s) => (
        <span key={s.id} className="star" style={{
          top: `${s.top}%`, left: `${s.left}%`, width: `${s.size}px`, height: `${s.size}px`,
          animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s`,
        }} />
      ))}
    </div>
  );
}

export default function Shortlist() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const isFree = (user?.subscription_tier || "free") === "free";

  // Mount-only fetch. `api` is a stable module import; setters are stable React refs.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/shortlist");
        setMatches(data.matches || []);
      } finally { setLoading(false); }
    })();
  }, []);

  // When user un-shortlists from a card here, remove it from the list.
  const handleShortlistChange = (candidateId, shortlisted) => {
    if (!shortlisted) {
      setMatches((prev) => prev.filter((m) => m.candidate_id !== candidateId));
    } else {
      setMatches((prev) => prev.map((m) => (m.candidate_id === candidateId ? { ...m, shortlisted } : m)));
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />

      {/* Celestial hero */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
        className="relative overflow-hidden"
        style={{ background: "#12103A", color: "white" }}
      >
        <StarField />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 relative">
          <div className="devanagari text-amber-300 text-sm">आपकी सूची</div>
          <div className="overline mt-2" style={{ color: "#FCD34D" }}>My Shortlist</div>
          <h1 className="font-serif-display text-4xl lg:text-6xl mt-3 leading-tight">
            The ones worth returning to.
          </h1>
          <p className="mt-4 text-stone-300 max-w-2xl">
            A private collection of matches you&apos;ve saved for a second look, a family conversation, or a longer decision.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 border border-white/15 rounded-full px-3 py-1.5 text-xs bg-white/5">
            <BookmarkCheck size={12} className="text-amber-300" />
            <span className="text-stone-300">
              {loading ? "Loading…" : `${matches.length} saved match${matches.length === 1 ? "" : "es"}`}
            </span>
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {loading && <div className="text-stone-500">Reading the stars…</div>}

        {!loading && matches.length === 0 && (
          <div className="border border-stone-200 rounded-2xl p-12 bg-white text-center max-w-2xl mx-auto">
            <Sparkles className="mx-auto text-amber-700" />
            <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-4">
              Your shortlist is quiet.
            </h2>
            <p className="mt-3 text-stone-600 text-sm leading-relaxed max-w-md mx-auto">
              Tap the bookmark on any match to keep it here — a private space to compare, revisit, and share with the elders when you&apos;re ready.
            </p>
            <Link
              to="/matches"
              data-testid="shortlist-empty-cta"
              className="btn-pill inline-flex items-center gap-2 mt-8 text-white text-sm px-5 py-2.5"
              style={{ background: "#1E1B4B" }}
            >
              Browse your Curated Set <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {!loading && matches.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.map((m, idx) => (
              <MatchCard
                key={m.candidate_id}
                match={m}
                index={idx}
                isFree={isFree}
                onShortlistChange={handleShortlistChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
